import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {Notification, NotificationStorageType} from './types';

const STORAGE_KEY = 'notifications';

const subscribe = (listener: (event: StorageEvent) => void) => {
    window.addEventListener('storage', listener);
    return () => {
        window.removeEventListener('storage', listener);
    };
};

const getSnapshot = () => {
    return localStorage.getItem(STORAGE_KEY);
};

const getNotifications = (notifications: string | null = null): Notification[] => {
    const sourceData = notifications ? notifications : getSnapshot();
    const parsedData: Notification[] = JSON.parse(sourceData || '[]');
    return parsedData.map((notification: Notification) => {
        // Convert the href into an `onClickItem`
        const {href} = notification;
        if (typeof href === 'string') {
            notification.onClickItem = () => {
                return window.location.assign(href);
            };
        }
        return notification;
    });
};

const setNotifications = (notifications: Notification[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    // eslint-disable-next-line i18next/no-literal-string
    window.dispatchEvent(new StorageEvent('storage', {key: STORAGE_KEY}));
};

export const addNotification = (notification: Notification) => {
    const notifications = getNotifications();
    setNotifications([
        // If a notification already exists with this ID, remove it.
        // There is currently only one notification ID, so it's not possible to get full coverage.
        ...notifications.filter(/* istanbul ignore next */ n => n.id !== notification.id),
        notification,
    ]);
};

export const removeNotification = (id: Notification['id']) => {
    const notifications = getNotifications();
    setNotifications(notifications.filter(notification => notification.id !== id));
};

export const purgeNotifications = () => {
    setNotifications([]);
};

export const useNotifications = (): NotificationStorageType => {
    const sourceData = useSyncExternalStore(subscribe, getSnapshot);
    const notifications = getNotifications(sourceData);

    return {
        notifications,
        addNotification,
        removeNotification,
        purgeNotifications,
    };
};
